import { ReactNode } from 'react';
import { Box, Card, CardContent, Chip, Divider, Grid, Typography } from '@mui/material';
import { useLanguage } from '../../../../../context/LanguageContext';
import { CHANNEL_LABELS } from '../channels';
import { isRichSms, readSmsMessage } from '../creationPayload';
import type { DefinitionFormData } from '../CampaignDefinitionStep';
import type { TargetingFormData } from '../CampaignTargetingStep';
import type { OfferFormData } from '../offers/offerForm';
import { formatPeriod, formatSchedule } from './formatters';
import OfferSummaryList from './OfferSummaryList';

interface CampaignSummaryCardProps {
  definition: DefinitionFormData;
  targeting: TargetingFormData;
  offers: OfferFormData[];
  hasCreation: boolean;
  creationPayload: Record<string, unknown> | null;
}

function SummaryField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={700} component="div">{value}</Typography>
    </Box>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <>
      <Divider sx={{ my: 1.5 }} />
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
        {children}
      </Typography>
    </>
  );
}

const chipSx = { fontSize: '0.7rem' };

const targetingLabel = (targeting: TargetingFormData) => {
  if (targeting.cashdeskTarget) return 'Type de ciblage';
  return targeting.listId !== null ? 'Liste' : 'Requête';
};

const targetingValue = (targeting: TargetingFormData) => {
  if (!targeting.queryDescription) return '—';
  const prefix = targeting.queryId !== null && !targeting.cashdeskTarget ? `#${targeting.queryId} — ` : '';
  return `${prefix}${targeting.queryDescription}`;
};

function CampaignSummaryCard({ definition, targeting, offers, hasCreation, creationPayload }: CampaignSummaryCardProps) {
  const { t } = useLanguage();
  const schedule = formatSchedule(definition);
  const followUp = formatPeriod(definition.followUpDateStart, definition.followUpDateEnd);
  const smsMessage = definition.channel === 'sms' ? readSmsMessage(creationPayload) : '';

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="body2" fontWeight={700}>{definition.title || '—'}</Typography>
        {schedule && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {schedule}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          <Chip label="Brouillon" size="small" sx={chipSx} />
          {definition.channel && (
            <Chip label={CHANNEL_LABELS[definition.channel]} size="small" sx={{ ...chipSx, bgcolor: 'badge.blue.bg', color: 'badge.blue.fg' }} />
          )}
          {definition.isPreEdited && <Chip label="Pré-éditée" size="small" sx={chipSx} />}
          {definition.isRecurring && <Chip label="Récurrente" size="small" sx={chipSx} />}
          {definition.isTestOperation && <Chip label="Opération test" size="small" sx={{ ...chipSx, bgcolor: 'badge.orange.bg', color: 'badge.orange.fg' }} />}
        </Box>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          {definition.channel !== 'cashdesk' && (
            <Grid size={4}>
              <SummaryField label={t('pages.marketing.communication.sendByCustomer')} value={definition.sendByCustomer ? 'OUI' : 'NON'} />
            </Grid>
          )}
          <Grid size={4}>
            <SummaryField label="Template associé" value={hasCreation ? 'OUI' : 'NON'} />
          </Grid>
          <Grid size={4}>
            <SummaryField label="Offre(s) associée(s)" value={definition.hasOffer ? offers.length : 'NON'} />
          </Grid>
          {followUp && (
            <Grid size={12}>
              <SummaryField label="Période de suivi" value={followUp} />
            </Grid>
          )}
        </Grid>

        <SectionLabel>Ciblage</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid size={8}>
            <SummaryField label={targetingLabel(targeting)} value={targetingValue(targeting)} />
          </Grid>
          <Grid size={4}>
            <SummaryField
              label="Nb. de destinataires"
              value={targeting.nbRecipients !== null ? targeting.nbRecipients.toLocaleString('fr-FR') : '—'}
            />
          </Grid>
        </Grid>

        {smsMessage && (
          <>
            <SectionLabel>Message SMS{isRichSms(creationPayload) ? ' (enrichi)' : ''}</SectionLabel>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
              {smsMessage}
            </Typography>
          </>
        )}

        {offers.length > 0 && (
          <>
            <SectionLabel>Offre(s)</SectionLabel>
            <OfferSummaryList offers={offers} />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default CampaignSummaryCard;
